// Helpers for AdminSendInvoice. The edge function (create-retailer-invoice/validate.ts) is the
// authority; these exist so the form can say what is wrong before a round trip, and so that
// dollars typed by a person become exact integer cents.

export const INVOICE_CURRENCIES = ["CAD", "USD"] as const;
export type InvoiceCurrency = (typeof INVOICE_CURRENCIES)[number];

/** Mirrors MAX_AMOUNT_CENTS in the edge function ($50,000). */
export const MAX_INVOICE_CENTS = 5_000_000;
export const MIN_INVOICE_CENTS = 100;

/**
 * "3600", "3,600", "3600.5", "$3,600.00" -> cents. Returns null for anything that is not a
 * plain amount with at most two decimals. Works on the digits, never on a float, so
 * 19.99 is exactly 1999 and not 1998.9999999999998.
 */
export function parseAmountToCents(input: string): number | null {
  const trimmed = input.trim().replace(/^\$\s*/, "");
  // Commas are accepted only as proper thousands separators (3,600 / 12,345.50).
  if (trimmed.includes(",") && !/^\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(trimmed)) return null;
  const cleaned = trimmed.replace(/,/g, "");
  const m = /^(\d{1,7})(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!m) return null;
  const cents = Number(m[1]) * 100 + (m[2] ? Number(m[2].padEnd(2, "0")) : 0);
  return Number.isSafeInteger(cents) ? cents : null;
}

export function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-CA", { style: "currency", currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export interface InvoiceFormInput {
  retailerId: string | null;
  amount: string;
  currency: string;
  termStart: string;
  termEnd: string;
  description: string;
}

/** First problem with the form, or null when it is ready to send. */
export function validateInvoiceForm(f: InvoiceFormInput): string | null {
  if (!f.retailerId) return "Choose the retailer to invoice.";
  const cents = parseAmountToCents(f.amount);
  if (cents === null) return "Enter the amount as a number, e.g. 3600 or 3600.50.";
  if (cents < MIN_INVOICE_CENTS || cents > MAX_INVOICE_CENTS) {
    return `The amount must be between ${formatMoney(MIN_INVOICE_CENTS, "USD")} and ${formatMoney(MAX_INVOICE_CENTS, "USD")}.`;
  }
  if (!(INVOICE_CURRENCIES as readonly string[]).includes(f.currency)) return "Choose CAD or USD.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.termStart)) return "Enter the term start date.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.termEnd)) return "Enter the term end date.";
  if (f.termEnd < f.termStart) return "The term end cannot be before the term start.";
  if (f.description.trim().length < 3) return "Add a description for the invoice line.";
  return null;
}

// ── Subscription form (AdminSendInvoice, "Create subscription and send invoice") ──

/** Mirrors MAX_LOCATIONS in create-retailer-subscription/validate.ts. */
export const MAX_LOCATIONS = 500;

/** CAD for Canadian retailers, USD for US ones, null for anything else (the admin cannot mix). */
export function currencyForCountry(country: string | null | undefined): InvoiceCurrency | null {
  const c = (country ?? "").trim().toUpperCase();
  if (c === "CA" || c === "CANADA") return "CAD";
  if (c === "US" || c === "USA" || c === "UNITED STATES") return "USD";
  return null;
}

export interface SubscriptionFormInput {
  retailerId: string | null;
  retailerIsDemo: boolean;
  retailerCountry: string | null;
  unitAmount: string;
  locations: string;
  startDate: string;
  todayIso: string;
}

/** First problem with the subscription form, or null when it is ready to send. */
export function validateSubscriptionForm(f: SubscriptionFormInput): string | null {
  if (!f.retailerId) return "Choose the retailer.";
  if (f.retailerIsDemo) return "Demo retailers cannot be billed.";
  if (currencyForCountry(f.retailerCountry) === null) return "This retailer's country is not CA or US, so no billing currency can be chosen.";
  const cents = parseAmountToCents(f.unitAmount);
  if (cents === null) return "Enter the annual price per location as a number, e.g. 1200 or 1200.50.";
  if (cents < MIN_INVOICE_CENTS || cents > MAX_INVOICE_CENTS) {
    return `The per-location price must be between ${formatMoney(MIN_INVOICE_CENTS, "USD")} and ${formatMoney(MAX_INVOICE_CENTS, "USD")}.`;
  }
  const n = Number(f.locations);
  if (!/^\d+$/.test(f.locations.trim()) || n < 1 || n > MAX_LOCATIONS) return `Locations must be a whole number from 1 to ${MAX_LOCATIONS}.`;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.startDate)) return "Enter the start date.";
  if (f.startDate < f.todayIso) return "The start date cannot be in the past.";
  return null;
}
